'use client'

import { isDefined } from '@morpho-org/morpho-ts'
import { useState } from 'react'

interface Predicate<T> {
  (prev: T | undefined, next: T): boolean
}

const hasChanged = <T>(prev: T | undefined, next: T) => isDefined(next) && !Object.is(prev, next)

/**
 * Hook that caches a value and only updates when a predicate returns true.
 * Useful for preventing unnecessary re-renders in performance-critical components like charts.
 * @param value - The current value to potentially cache
 * @param shouldUpdate - Predicate function to determine if value should update (defaults to reference equality check)
 * @returns The cached value (previous if predicate returns false, new if true)
 * Object/array values must be referentially stable across renders (memoized or prop-stable); an inline literal would re-trigger the render-phase update on every render.
 */
export function useCachedValue<T>(value: T, shouldUpdate: Predicate<T> = hasChanged) {
  const [cachedValue, setCachedValue] = useState(() => value)
  if (!Object.is(value, cachedValue) && shouldUpdate(cachedValue, value)) {
    setCachedValue(() => value)
    return value
  }
  return cachedValue
}
